<template>
    <div
        class="patent-details"
        :class="[{ inline: props.inline }, { absolute: !props.inline }]"
        ref="patentDetailsRef"
        tabindex="0"
    >
        <div v-if="!props.inline" class="nav-tab display-flex flex-column align-items-center">
            <font-awesome-icon
                :icon="`fas fa-xmark`"
                class="p-sm px-sm bg-stone-200 patent-nav close text-stone-700"
                :class="{ disabled: isNavigationDisabled }"
                v-tooltip="{
                    content: 'Close',
                    placement: 'left',
                    delay: { show: 500, hide: 0 },
                }"
                @click="closeDetails"
                @keydown.enter="closeDetails"
                @keydown.space="closeDetails"
            />
            <div class="divide hor"></div>
            <font-awesome-icon
                :icon="`fas fa-chevron-down`"
                class="p-sm mb-sm bg-stone-200 patent-nav up text-stone-700"
                :class="{ disabled: isNavigationDisabled }"
                size="sm"
                v-tooltip="{
                    content: 'Previous Patent',
                    placement: 'left',
                    delay: { show: 500, hide: 0 },
                }"
                @click="previousPatent"
                @keydown.enter="previousPatent"
                @keydown.space="previousPatent"
            />
            <font-awesome-icon
                :icon="`fas fa-chevron-down`"
                class="p-sm bg-stone-200 patent-nav down text-stone-700"
                :class="{ disabled: isNavigationDisabled }"
                v-tooltip="{
                    content: 'Next Patent',
                    placement: 'left',
                    delay: { show: 500, hide: 0 },
                }"
                @click="nextPatent"
                @keydown.enter="nextPatent"
                @keydown.space="nextPatent"
            />
        </div>
        <div class="contain-shadow px-md" ref="containShadowRef">
            <PatentHeader
                v-if="!props.inline"
                ref="highLevelRef"
                :patentDetails="patentViewerStore.patentDetails || {}"
                :allowPruning="portfolioManagerStore.allowPruning"
                :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
                :shouldHideInfo="shouldHideInfo"
                :titleEllipsis="titleEllipsis"
                @prune-patent="prunePatent"
            />

            <div
                class="anchor-tabs display-flex align-items-end mb-xl sticky-tabs"
                ref="anchorTabsRef"
                :class="{ 'top-position': shouldHideInfo }"
            >
                <div
                    v-for="tab in anchorTabs"
                    class="tab px-md pb-sm"
                    :class="{ active: tab.id === activeTab }"
                    @click="scrollTo(tab.id)"
                >
                    <p class="geist-font text-stone-800 semi-bold">{{ tab.title }}</p>
                </div>
            </div>

            <template v-if="showAISummary">
                <PatentAISummary
                    class="mb-md"
                    :patentDetails="patentViewerStore.patentDetails || {}"
                    :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
                />
            </template>

            <PatentOverview
                :patentDetails="patentViewerStore.patentDetails || {}"
                :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
            />

            <template v-if="shouldShowProductsMap">
                <PatentMappedProducts
                    ref="patentProductMapRef"
                    :patentDetails="patentViewerStore.patentDetails || {}"
                    :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
                />
                <div class="divide full my-xl"></div>
            </template>

            <template v-if="showPatentSim">
                <PatentSimilarity
                    ref="patentSimilarityRef"
                    :patentDetails="patentViewerStore.patentDetails || {}"
                    :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
                />
            </template>

            <!-- Accordions -->
            <div class="accor-panel mb-xl">
                <AccordionPanel label="Claims" id="claims">
                    <AonSkeleton
                        class="w-100 display-flex align-items-center mb-sm"
                        :loading="patentViewerStore.loadingPatentDetail"
                        :data="0"
                        :height="'80px'"
                        light
                    >
                        <p class="py-md" v-html="patentViewerStore.patentDetails?.claims"></p>
                        <p v-if="!patentViewerStore.patentDetails?.claims">No claims found</p>
                    </AonSkeleton>
                </AccordionPanel>
            </div>
            <div class="accor-panel mb-xl">
                <AccordionPanel label="Description" id="description">
                    <AonSkeleton
                        class="w-100 display-flex align-items-center mb-sm"
                        :loading="patentViewerStore.loadingPatentDetail"
                        :data="0"
                        :height="'80px'"
                        light
                    >
                        <p class="py-md" v-html="patentViewerStore.patentDetails?.description"></p>
                        <p v-if="!patentViewerStore.patentDetails?.description">
                            No description found
                        </p>
                    </AonSkeleton>
                </AccordionPanel>
            </div>
            <div class="accor-panel mb-xl">
                <AccordionPanel label="Patent Family Members" id="family">
                    <AonSkeleton
                        class="w-100 display-flex align-items-center mb-sm flex-wrap"
                        :loading="patentViewerStore.loadingPatentDetail"
                        :data="0"
                        :height="'80px'"
                        light
                    >
                        <div
                            v-for="patent in patentViewerStore.patentDetails?.familyPatents"
                            :key="patent.patentId"
                            class="slide-item mt-sm display-flex justify-center w-25"
                            @click="goToGooglePatents(patent.googlePatentsLink)"
                        >
                            <p class="link bold">
                                {{ patent.patentId }}
                            </p>
                        </div>
                    </AonSkeleton>
                </AccordionPanel>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
import { usePatentViewerStore, usePortfolioManagerStore, useArenaOutputStore } from '@/stores'

import PatentHeader from '@/components/patentViewer/patentDetailComponents/PatentHeader.vue'
import PatentOverview from '@/components/patentViewer/patentDetailComponents/PatentOverview.vue'
import PatentMappedProducts from '@/components/patentViewer/patentDetailComponents/PatentMappedProducts.vue'
import PatentSimilarity from '@/components/patentViewer/patentDetailComponents/PatentSimilarity.vue'
import PatentAISummary from '@/components/patentViewer/patentDetailComponents/PatentAISummary.vue'

import AccordionPanel from '@/components/helpers/AccordionPanel.vue'

import { debounce } from 'lodash'
import { useFlag } from '@unleash/proxy-client-vue'

const patentViewerStore = usePatentViewerStore()
const portfolioManagerStore = usePortfolioManagerStore()
const arenaOutputStore = useArenaOutputStore()

const productMapFlagEnabled = useFlag('ipAlpha.AAPatentProductMap')
const showPatentSim = useFlag('ipAlpha.AAPatentSim')
const showAISummary = useFlag('ipAlpha.AAPatentAISummary')
const eventBus = inject('eventBus')

const props = defineProps({
    hideMappedProducts: {
        type: Boolean,
        default: false,
    },
    inline: {
        type: Boolean,
        default: false,
    },
})

const patentDetailsRef = ref(null)
const patentSimilarityRef = ref(null)
const patentProductMapRef = ref(null)

const activeTab = ref('overview')

// Sticky functionality
const titleEllipsis = ref(false)
const containShadowRef = ref(null)
const highLevelRef = ref(null)
const anchorTabsRef = ref(null)
const lastScrollTop = ref(0)
const shouldHideInfo = ref(false)
const userSelectedTab = ref(null)

onMounted(() => {
    window.addEventListener('keydown', handleKeyPress)
    if (containShadowRef.value) {
        containShadowRef.value.addEventListener('scroll', handleScroll)
    }
})

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyPress)
    if (containShadowRef.value) {
        containShadowRef.value.removeEventListener('scroll', handleKeyPress)
    }
})

watch(
    () => patentViewerStore.targetPatentId,
    (newVal, oldVal) => {
        if (props.inline) return
        if (!newVal) {
            patentViewerStore.patentDetailsOpen = false
            return
        }

        if (newVal !== oldVal) {
            patentViewerStore.patentDetailsOpen = true
        }
    }
)

watch(
    () => patentViewerStore.patentDetailsOpen,
    (isOpen) => {
        if (props.inline) return
        if (isOpen) {
            patentDetailsRef.value?.focus()
        } else {
            patentViewerStore.patentDetails = null
        }
    }
)

const shouldShowProductsMap = computed(() => productMapFlagEnabled && !props.hideMappedProducts)

const isNavigationDisabled = computed(() => {
    const isSimilarityLoading = patentSimilarityRef.value?.loadingSimilarity || false
    const isProductsLoading = patentProductMapRef.value?.loadingProducts || false
    return isSimilarityLoading || isProductsLoading
})

const anchorTabs = computed(() => {
    let tabs = [{ title: 'Overview', id: 'overview' }]

    if (productMapFlagEnabled.value && withinProductMapBounds.value && !props.hideMappedProducts) {
        tabs.push({ title: 'Mapped Products', id: 'products' })
    }

    if (showPatentSim.value) {
        tabs.push({ title: 'Similar Patents', id: 'similar' })
    }

    return tabs
})

const withinProductMapBounds = computed(() => {
    return (
        (patentViewerStore.patentDetails?.status === 'Active' &&
            patentViewerStore.patentDetails?.patentId.startsWith('US-')) ||
        (patentViewerStore.patentDetails?.status === 'Active' &&
            patentViewerStore.patentDetails?.patentId.startsWith('US-') &&
            arenaOutputStore.targetCompany[0].aonEntityPk)
    )
})

const scrollTo = (index) => {
    const element = document.getElementById(index)
    if (element && containShadowRef.value) {
        activeTab.value = index
        userSelectedTab.value = index

        if (index === 'products') {
            if (
                patentProductMapRef.value &&
                patentViewerStore.patentDetails?.publicationPk &&
                !patentViewerStore.mappedProductsExistInCache(
                    patentViewerStore.patentDetails.publicationPk
                )
            ) {
                patentProductMapRef.value.onRunProductMapping()
            }
        }

        if (index === 'similar') {
            if (
                patentSimilarityRef.value &&
                patentViewerStore.patentDetails?.publicationPk &&
                !patentViewerStore.similarPatentsExistInCache(
                    patentViewerStore.patentDetails.publicationPk
                )
            ) {
                patentSimilarityRef.value.onFindSimilarPatents()
            }
        }

        // Get the height of sticky elements to use as offset
        const headerHeight = highLevelRef.value?.offsetHeight || 0
        const offset = headerHeight
        const elementPosition = element.offsetTop - 140

        // Only scroll if not already at the target position
        if (Math.abs(containShadowRef.value.scrollTop - elementPosition) > 10) {
            containShadowRef.value.scrollTo({
                top: elementPosition,
                behavior: 'smooth',
            })
        }
    }
}

const updateActiveTabOnScroll = () => {
    if (!containShadowRef.value) return

    if (userSelectedTab.value) {
        const selectedElement = document.getElementById(userSelectedTab.value)
        if (selectedElement) {
            const rect = selectedElement.getBoundingClientRect()
            // If the selected element is still visible in the viewport (with some tolerance)
            const headerHeight = highLevelRef.value?.highLevelRef?.offsetHeight || 0
            const tabsHeight = anchorTabsRef.value?.offsetHeight || 0
            const offset = headerHeight + tabsHeight + 20

            // If element is still in view, keep the user's selection
            if (rect.top >= -200 && rect.bottom <= window.innerHeight + 200) {
                activeTab.value = userSelectedTab.value
                return
            }
        }
    }

    const sections = anchorTabs.value.map((tab) => document.getElementById(tab.id))

    // Get the height of sticky elements to use as offset
    const headerHeight = highLevelRef.value?.highLevelRef?.offsetHeight || 0
    const tabsHeight = anchorTabsRef.value?.offsetHeight || 0
    const offset = headerHeight + tabsHeight + 20 // Adding extra padding

    const scrollPosition = containShadowRef.value.scrollTop + offset

    // Check if at the bottom of the scroll container
    const isAtBottom =
        containShadowRef.value.scrollHeight - containShadowRef.value.scrollTop <=
        containShadowRef.value.clientHeight + 5 // 5px tolerance

    // If at bottom and user has explicitly selected a tab, respect that selection
    if (isAtBottom && userSelectedTab.value) {
        activeTab.value = userSelectedTab.value
        return
    }

    // If at bottom but no explicit user selection, use the default bottom behavior
    if (isAtBottom && sections.length > 0) {
        const lastVisibleTabIndex = sections.reduce((lastIndex, section, index) => {
            if (section) return index
            return lastIndex
        }, 0)

        if (activeTab.value !== anchorTabs.value[lastVisibleTabIndex].id) {
            activeTab.value = anchorTabs.value[lastVisibleTabIndex].id
        }
        return
    }

    // tab detection logic for non-bottom scroll position
    for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
            if (activeTab.value !== anchorTabs.value[i].id) {
                activeTab.value = anchorTabs.value[i].id
                // Clear user selection when naturally scrolling to a different section
                if (userSelectedTab.value !== anchorTabs.value[i].id) {
                    userSelectedTab.value = null
                }
            }
            break
        }
    }
}

const handleScroll = debounce(() => {
    if (!containShadowRef.value) return
    const scrollTop = containShadowRef.value.scrollTop

    if (scrollTop > 50) {
        titleEllipsis.value = true
    } else {
        titleEllipsis.value = false
    }

    // Determine scroll direction
    if (scrollTop > lastScrollTop.value && scrollTop > 50) {
        shouldHideInfo.value = true
    } else if (scrollTop < lastScrollTop.value || scrollTop <= 50) {
        shouldHideInfo.value = false
    }

    lastScrollTop.value = scrollTop

    updateActiveTabOnScroll()
}, 10)

const previousPatent = () => {
    eventBus.emit('previous-patent')
}

const nextPatent = () => {
    eventBus.emit('next-patent')
}

const closeDetails = async () => {
    eventBus.emit('close-patent')
    patentViewerStore.patentDetailsOpen = false
}

const handleKeyPress = (event) => {
    if (!patentViewerStore.patentDetailsOpen) return
    if (!patentDetailsRef.value?.contains(document.activeElement)) return
    if (isNavigationDisabled.value) return

    switch (event.key) {
        case 'ArrowUp':
            previousPatent()
            break
        case 'ArrowDown':
            nextPatent()
            break
    }
}

const goToGooglePatents = (googleLink) => {
    window.open(`${googleLink}`, '_blank')
}

const prunePatent = async () => {
    if (!patentViewerStore.patentDetails) return
    patentViewerStore.patentDetails.prune = !patentViewerStore.patentDetails.prune
    eventBus.emit('prune-patent', patentViewerStore.patentDetails)
}
</script>

<style lang="scss" scoped>
@forward '@/styles/new_styles.scss';

.patent-details {
    width: 1000px;
    height: 100%;
    background-color: white;
    box-shadow: -8px 0px 76px 0px rgba(0, 0, 0, 0.25);

    position: absolute;
    z-index: 12;
    right: 0;
    top: 0;

    &:focus {
        outline: none;
    }

    .abstract-holder {
        min-height: 80px;
    }

    .prune-button {
        background: var(--color-stone-400);
        padding: 10px 16px;
        border-radius: 8px;

        transition: all 0.2s ease-in-out;

        &:hover {
            background: var(--color-stone-400);
        }
    }

    :deep(.not-pruned) path {
        fill: var(--color-stone-300) !important;
    }

    :deep(.pruned) path {
        fill: var(--color-success) !important;
    }

    .accor-panel {
        p {
            white-space: pre-line;
        }

        .accordion {
            border-bottom: 1px solid var(--color-stone-400);

            &:first-of-type {
                border-top: 1px solid var(--color-stone-400);
            }
        }
    }

    .nav-tab {
        padding: 10px;
        background: white;
        border-radius: 8px 0 0 8px;
        overflow: hidden;

        position: absolute;
        z-index: -1;
        top: 0;
        left: -58px;

        box-shadow: rgba(0, 0, 0, 0.1) -10px 6px 20px -4px;

        .patent-nav {
            width: 18px;
            height: 18px;
            cursor: pointer;
            border-radius: 4px;
            padding: 10px !important;

            transition: all 0.3s ease-in-out;

            &:hover {
                background-color: var(--color-stone-300) !important;
            }

            &.up {
                transform: rotate(180deg);
            }
        }
    }

    .anchor-tabs {
        border-bottom: 1px solid rgba(#5e7394, 0.2);
        min-height: 58px;

        .tab {
            border-bottom: solid 5px transparent;
            cursor: pointer;

            &.active {
                border-color: var(--color-gold);
            }
        }
    }

    .contain-shadow {
        width: 100%;
        height: 100vh;
        overflow-y: auto;
        overflow-x: hidden;

        position: relative;
    }

    .no-wrap {
        white-space: nowrap;
    }

    .divide {
        border-radius: 50%;
        width: 5px;
        height: 5px;
        background-color: var(--color-stone-200);
        margin: 0 12px;

        &.hor {
            border-radius: 0;
            width: 25px;
            height: 2px;
            margin: 12px 0;
        }

        &.full {
            border-radius: 0;
            width: 100%;
            height: 1px;
            background-color: var(--color-stone-200);
        }
    }

    .sticky-header {
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: white;

        &.expanded {
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
    }

    .sticky-tabs {
        position: sticky;
        top: 95px;
        z-index: 9;
        background-color: white;

        &.top-position {
            top: 95px;
            z-index: 10;
        }
    }

    .assignee,
    .dates {
        height: 40px;
        transform-origin: top;

        &.hide-info {
            opacity: 0;
            transform: scaleY(0);
            position: absolute;
            pointer-events: none;
        }
    }

    .info-spacer {
        height: 0;

        &.active {
            height: 96px;
        }
    }

    .status-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
    }

    /* Inline display mode overrides */
    &.inline {
        background-color: var(--color-stone-50) !important;
        position: static !important;
        right: 0 !important;
        top: 0 !important;
        width: 100% !important;
        height: auto !important;
        box-shadow: none !important;
        padding-top: 16px;

        .contain-shadow {
            height: auto !important;
            max-height: none !important;
        }

        .nav-tab {
            position: static !important;
            transform: translate(0, 0) !important;
            box-shadow: none !important;
            margin-bottom: 8px;
        }

        .anchor-tabs {
            top: 0;
            display: none;
        }
    }
}
</style>
